/**
 * PdfCanvasViewer — shows a PDF by drawing its pages onto <canvas> with the
 * bundled pdf.js (CHG-0097).
 *
 * Why not an <iframe>: bank PCs have the built-in PDF viewer turned off by group
 * policy, inside the Teams desktop app too, so an iframe PDF is an empty grey box
 * with no error. Mackenzie's Signature Card was one (CHG-0097); the RCP kiosk hit
 * the same thing (BACKLOG-1539, KioskDocumentViewer, which this follows).
 *
 * Source: pass the bytes — a Blob, a Uint8Array or a `data:` URL. A `blob:` URL
 * string is accepted as a last resort but may be refused: the app's CSP
 * connect-src does not list blob:, and fetch() of a blob: URL is checked against
 * it. Every caller in this app holds the Blob, so it passes that.
 *
 * Fails loudly, never blank: if pdf.js can't read the file the box says so and
 * offers "Download the document" (Teams desktop cannot open a blob: URL in a new window).
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { dataUrlToBytes, loadPdfJs, type PdfDocumentProxy, type PdfPageProxy } from '@/services/pdfjs';
import './PdfCanvasViewer.css';

export type PdfSource = Blob | Uint8Array | string;

export interface PdfCanvasViewerProps {
  source: PdfSource | null | undefined;
  /** Names the document for screen readers and the error text. */
  title: string;
  /** Height of the scrolling box (CSS length). Omit it to size the box with `className`. */
  height?: CSSProperties['height'];
  className?: string;
}

/** Cap the backing-store scale: 2x is crisp without quadrupling memory. */
const MAX_DPR = 2;
/** Horizontal room around a page inside the scroller (CSS px), matches the CSS padding. */
const PAGE_GUTTER = 12;

async function toBytes(source: PdfSource): Promise<Uint8Array> {
  if (source instanceof Uint8Array) return source.slice();
  if (source instanceof Blob) return new Uint8Array(await source.arrayBuffer());
  if (source.startsWith('data:')) {
    const bytes = dataUrlToBytes(source);
    if (!bytes) throw new Error('The document data could not be read.');
    return bytes;
  }
  const r = await fetch(source);
  if (!r.ok) throw new Error(`The document could not be loaded (HTTP ${r.status}).`);
  return new Uint8Array(await r.arrayBuffer());
}

/** pdf.js rejects an in-flight render with this when we cancel it to redraw. */
function isRenderCancellation(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name ?? '';
  return name === 'RenderingCancelledException' || /cancelled/i.test(err instanceof Error ? err.message : '');
}

function PdfPageCanvas({ page, pageNumber, pageCount, cssWidth }: {
  page: PdfPageProxy;
  pageNumber: number;
  pageCount: number;
  cssWidth: number;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);
  const [text, setText] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || cssWidth <= 0) return undefined;
    let cancelled = false;
    // Draw at cssWidth x devicePixelRatio, shown at cssWidth, so small print stays sharp.
    const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), MAX_DPR);
    const unscaled = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: (cssWidth / unscaled.width) * dpr });
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${Math.floor(cssWidth)}px`;
    canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
    const task = page.render({ canvas, viewport });
    task.promise.then(
      () => { if (!cancelled) setFailed(false); },
      (err: unknown) => { if (!cancelled && !isRenderCancellation(err)) setFailed(true); },
    );
    return () => {
      cancelled = true;
      task.cancel();
    };
  }, [page, cssWidth]);

  // The canvas carries no text; give screen readers the page's text instead.
  useEffect(() => {
    let cancelled = false;
    void page.getTextContent().then(
      (content) => {
        if (!cancelled) {
          setText(content.items.map((i) => ('str' in i ? i.str : '')).join(' ').replace(/\s+/g, ' ').trim());
        }
      },
      () => { /* no text layer; the canvas still shows */ },
    );
    return () => { cancelled = true; };
  }, [page]);

  return (
    <div className="pdf-canvas__page">
      <canvas ref={canvasRef} className="pdf-canvas__canvas" aria-hidden="true" />
      <span className="pdf-canvas__sr">{`Page ${pageNumber} of ${pageCount}. ${text}`}</span>
      {failed ? (
        <p className="pdf-canvas__page-fail" role="alert">Page {pageNumber} could not be displayed.</p>
      ) : pageCount > 1 ? (
        <p className="pdf-canvas__page-label" aria-hidden="true">Page {pageNumber} of {pageCount}</p>
      ) : null}
    </div>
  );
}

export function PdfCanvasViewer({ source, title, height, className }: PdfCanvasViewerProps): JSX.Element {
  const [pages, setPages] = useState<PdfPageProxy[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [pageWidth, setPageWidth] = useState(0);

  // "Download the document" fallback: a PDF-typed blob URL of the same bytes (saved, not opened: Teams desktop refuses blob windows).
  const fallbackUrl = useMemo(() => {
    if (!failure || !source) return null;
    if (typeof source === 'string') return source;
    const blob = source instanceof Blob ? source : new Blob([source.slice()], { type: 'application/pdf' });
    return URL.createObjectURL(blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' }));
  }, [failure, source]);
  useEffect(() => () => {
    if (fallbackUrl && typeof source !== 'string') URL.revokeObjectURL(fallbackUrl);
  }, [fallbackUrl, source]);

  useEffect(() => {
    setPages(null);
    setFailure(null);
    if (!source) return undefined;
    let cancelled = false;
    let doc: PdfDocumentProxy | null = null;
    void (async () => {
      try {
        const data = await toBytes(source);
        if (cancelled) return;
        const pdfjs = await loadPdfJs();
        if (cancelled) return;
        doc = await pdfjs.getDocument({ data }).promise;
        if (cancelled) return;
        if (doc.numPages < 1) throw new Error('The document has no pages.');
        const loaded = await Promise.all(
          Array.from({ length: doc.numPages }, (_, i) => (doc as PdfDocumentProxy).getPage(i + 1)),
        );
        if (!cancelled) setPages(loaded);
      } catch (e) {
        if (!cancelled) setFailure(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
      void doc?.destroy().catch(() => { /* already torn down */ });
    };
  }, [source]);

  // Fit pages to the box and redraw when it resizes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = (): void => {
      const w = Math.max(0, el.clientWidth - PAGE_GUTTER * 2);
      setPageWidth((prev) => (Math.abs(prev - w) > 1 ? w : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pages]);

  const boxClass = ['pdf-canvas', className].filter(Boolean).join(' ');

  if (failure) {
    return (
      <div className={`${boxClass} pdf-canvas--message`} style={height === undefined ? undefined : { height }} role="alert">
        <p className="pdf-canvas__error-title">This document can&apos;t be displayed here</p>
        <p className="pdf-canvas__error-text">{title}: {failure}</p>
        {fallbackUrl && (
          <a className="pdf-canvas__open" href={fallbackUrl} download={`${title.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'document'}.pdf`} rel="noopener">
            Download the document
          </a>
        )}
      </div>
    );
  }
  if (!pages) {
    return (
      <div className={`${boxClass} pdf-canvas--message`} style={height === undefined ? undefined : { height }} role="status">
        Loading the document…
      </div>
    );
  }
  return (
    <div ref={scrollRef} className={boxClass} style={height === undefined ? undefined : { height }} role="region" aria-label={title} tabIndex={0}>
      {pages.map((p, i) => (
        <PdfPageCanvas key={i} page={p} pageNumber={i + 1} pageCount={pages.length} cssWidth={pageWidth} />
      ))}
    </div>
  );
}
